import React, { useEffect } from 'react';
import { IntlShape, injectIntl } from 'react-intl';
import { get, isEmpty } from 'lodash';
import { connect } from 'react-redux';
import { rcCoporeal } from 'containers/CourtManagerLegalProceedings/constants';
import ClaimInformation from 'containers/PreOpeningPage/ClaimInformation';
import ClaimCircumstances from 'containers/PreOpeningPage/ClaimCircumstances';
import VictimCard from 'containers/PreOpeningPage/VictimCard';
import { Listings } from 'containers/AppContainer/typings';
import { EMPTY_CLAIM_TO_PREOPEN } from 'containers/data-structure';
import Breadcrumbs from 'containers/RecapManagerPage/Breadcrumbs';
import { claimStatusLabel, getCity } from 'containers/RecapManagerPage/utils';
import { getValueByCode } from 'containers/common/LegalProceedings/JudgementAdd/Recap/utils';
import {
  GlobalBloc,
  Container,
} from 'containers/LawyerRecapPage/styled-component';
import OpposingPartyRecapContainer from 'containers/PreOpeningPage/OpposingParty/OpposingPartyRecapContainer';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import FraudeInformation from 'containers/PreOpeningPage/fraude';
import JustificativeDocuments from 'components/JustificativeDocuments';
import { requestExpertiseMissions } from 'containers/CourtManagerAssignedExpertise/actions';
import { setClaimRecap } from 'containers/RecapManagerPage/actions';
import { resolveSagisClaim } from 'helpers/utils';
import { ClaimRecapType } from 'typings/claimTypes';

export interface ExpertRecapProps {
  intl: IntlShape;
  mission: any;
  listings: Listings;
  storedClaimToPreopen: any;
  requestClaimRecap: Function;
  requestExpertiseMissions: Function;
  claimId: number;
  claimRecap: ClaimRecapType;
  policyInfo: { sagis: {} };
  requestSearchClaims: Function;
  resetSearchClaimsStore: Function;
  shouldDisplayBreadCrumbs: boolean;
  className: string;
  shouldNotGetClaim?: boolean;
  stakeholders: any;
  setClaimRecap: Function;
  sagis_code: any;
  sagisOpposingParties: any;
  resetStoreClaim: Function;
}

const ExpertRecap: React.FC<ExpertRecapProps> = (props): JSX.Element => {
  const { mission } = props;
  useEffect(() => {
    return () => {
      props.resetStoreClaim();
    };
  }, []);

  const getClaimStatus = () => {
    const claim = get(props.claimRecap, 'claim', {});
    const status = get(claim, 'status', '');
    return claimStatusLabel(status, props);
  };

  const getClaimNumber = () => {
    const claim = get(props.claimRecap, 'claim', {});
    return get(claim, 'claimNumber', '');
  };

  const getExpertInfo = () => {
    const assignedExpert = get(props.mission, 'assignedExperts')?.find(
      (element: any) => element.expertCode === props.sagis_code,
    );
    const expertInfo =
      assignedExpert &&
      get(props.listings, 'intervenants')?.find(
        (item: any) =>
          Number(item.ItemKey) === Number(assignedExpert?.expertCode),
      );
    const expertTown =
      expertInfo?.townCode &&
      getCity(get(props.listings, 'villes', []), expertInfo?.townCode as any);
    return {
      ...expertInfo,
      expertTown: expertTown?.ItemDescription,
    };
  };
  const getTypeMission = () => {
    return getValueByCode(
      get(props.mission, 'type'),
      get(props.listings, 'expertise'),
    );
  };

  const getVictimMission = () => {
    const victim =
      get(props.mission, 'victims[0].firstName', '') +
      ' ' +
      get(props.mission, 'victims[0].lastName', '');
    return victim;
  };

  return (
    <GlobalBloc className={props.className}>
      {props.shouldDisplayBreadCrumbs && (
        <Breadcrumbs
          claimStatus={getClaimStatus()}
          claimNumber={getClaimNumber()}
        />
      )}
      {!isEmpty(props.claimRecap) && !isEmpty(props.policyInfo) && (
        <Container>
          <ClaimInformation
            isRecapPage
            isReadOnly
            isManager={false}
            inputProps={{ disableUnderline: true }}
            disableInput
            inputLabelProps
            listings={props.listings}
            claimToPreopen={EMPTY_CLAIM_TO_PREOPEN}
            storedClaimToPreopen={props.claimRecap}
            stakeholders={props.stakeholders}
            missionInfo={props.mission}
          />
          {featureService.hasFeature(FEATURES.OPPOSING_PARTY) && (
            <OpposingPartyRecapContainer
              isRecapPage
              claimToPreopen={EMPTY_CLAIM_TO_PREOPEN}
              storedClaimToPreopen={props.claimRecap}
              listings={props.listings}
              policyInfo={props.policyInfo}
              setClaimRecap={props.setClaimRecap}
              sagisOpposingParties={props.sagisOpposingParties}
            />
          )}
          <ClaimCircumstances
            isRecapPage
            isReadOnly
            isManager={false}
            inputProps={{ disableUnderline: true }}
            disableInput
            inputLabelProps
            listings={props.listings}
            claimToPreopen={EMPTY_CLAIM_TO_PREOPEN}
            storedClaimToPreopen={props.claimRecap}
            claimCircumstancesSagis={get(
              resolveSagisClaim(props.policyInfo),
              'offeredRisks',
              [],
            )?.find(
              (element: { riskName: string }) =>
                element.riskName === rcCoporeal,
            )}
          />

          <VictimCard
            isRecapPage
            isReadOnly
            isManager={false}
            inputProps={{ disableUnderline: true }}
            disableInput
            inputLabelProps
            listings={props.listings}
            claimToPreopen={EMPTY_CLAIM_TO_PREOPEN}
            storedClaimToPreopen={props.claimRecap}
          />
          <JustificativeDocuments
            documents={
              mission?.document
                ? [...props.claimRecap?.documents, mission?.document]
                : props.claimRecap?.documents
            }
            mission={{
              ...mission,
              expertInfo: getExpertInfo(),
              claimNumber: getClaimNumber(),
              missionType: getTypeMission(),
              victim: getVictimMission(),
            }}
          />
          {featureService.hasFeature(FEATURES.FRAUDE_CORPO) && (
            <FraudeInformation
              isRecapPage
              disableInput
              updateFilde={() => null}
              claimToPreopen={EMPTY_CLAIM_TO_PREOPEN}
              storedClaimToPreopen={props.claimRecap}
              listings={props.listings}
            />
          )}
        </Container>
      )}
    </GlobalBloc>
  );
};

const mapDispatchToProps = {
  requestExpertiseMissions,
  setClaimRecap,
};

export default connect(null, mapDispatchToProps)(injectIntl(ExpertRecap));
